import React from 'react'
import { useLocation } from 'react-router-dom'
import { MessageSquare, Minimize2, X, Send, Paperclip, CalendarPlus, Loader2 } from 'lucide-react'
import {
  atlas,
  ChatConversation,
  ChatMessage,
  ProviderProfession,
  enabledStacks,
  fetchAuthTokenForRealtime,
  isStackEnabled,
} from '../api'

type ChatOpenEventDetail = {
  profession?: ProviderProfession
  selectionId?: string
  patientId?: string
  specialistId?: string
  peerName?: string
  peerEmail?: string
  peerPhone?: string
  patientName?: string
  minimized?: boolean
}

type ChatDockProps = {
  enabled?: boolean
}

type ThreadFetchOptions = {
  cursor?: string | null
  append?: boolean
  quiet?: boolean
  limit?: number
  preserveExisting?: boolean
}

type MessageFetchOptions = {
  markRead?: boolean
  cursor?: string | null
  append?: boolean
  autoMarkRead?: boolean
}

type ConversationLookupResult = {
  found: ChatConversation | null
  merged: ChatConversation[]
  cursor: string | null
  pages: number
  exhausted: boolean
}

type ChatRealtimeEvent = {
  type?: string
  stack?: string | null
  conversationId?: string | null
  selectionId?: string | null
  ownUnreadCount?: number | null
  direction?: 'incoming' | 'outgoing' | string
  message?: ChatMessage | null
  sentAt?: string | null
  readAt?: string | null
}

const CONTACT_SLOT = 'REQUEST_BOOKING'
const CHAT_THREADS_PAGE_SIZE = 24
const CHAT_MESSAGES_PAGE_SIZE = 80
const CHAT_DEEP_LINK_PAGE_SIZE = 48
const CHAT_DEEP_LINK_RETRY_MS = 5000
const CHAT_DEEP_LINK_SCAN_PAGE_BUDGET = 30
const CHAT_DEEP_LINK_SCAN_MS_BUDGET = 2500
const CHAT_REALTIME_RECONNECT_BASE_MS = 1200
const CHAT_REALTIME_RECONNECT_MAX_MS = 20000

const chatConversationKey = (stack: ProviderProfession, conversationId: string) =>
  `${stack}:${conversationId}`

const messagePreview = (message?: ChatMessage | null) => {
  if (!message) return 'New message'
  const text = String(message.text || '').trim()
  if (text) return text
  if (message.booking) return 'Booking update'
  if (Array.isArray(message.attachments) && message.attachments.length) {
    return message.attachments.length > 1 ? `${message.attachments.length} attachments` : 'Attachment'
  }
  return 'New message'
}

const resolveProfession = (search = ''): ProviderProfession => {
  const params = new URLSearchParams(search || '')
  const raw = (params.get('stack') || '').trim().toLowerCase()
  if (raw && isStackEnabled(raw as ProviderProfession)) return raw as ProviderProfession
  return (enabledStacks[0] || 'doctor') as ProviderProfession
}

const initials = (name?: string | null) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (!parts.length) return 'CL'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase()
}

const fmtTime = (iso?: string | null) => {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString('en-AU', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

const mergeConversations = (base: ChatConversation[], incoming: ChatConversation[]) => {
  const merged: ChatConversation[] = []
  const indexById = new Map<string, number>()

  const upsert = (row: ChatConversation) => {
    const id = String(row?.conversationId || '').trim()
    if (!id) return
    const idx = indexById.get(id)
    if (typeof idx === 'number') {
      merged[idx] = { ...merged[idx], ...row }
      return
    }
    indexById.set(id, merged.length)
    merged.push(row)
  }

  base.forEach(upsert)
  incoming.forEach(upsert)
  merged.sort((a, b) => {
    const aMs = Number.isFinite(Date.parse(String(a?.lastMessageAt || ''))) ? Date.parse(String(a?.lastMessageAt || '')) : 0
    const bMs = Number.isFinite(Date.parse(String(b?.lastMessageAt || ''))) ? Date.parse(String(b?.lastMessageAt || '')) : 0
    return bMs - aMs
  })
  return merged
}

const prependMessages = (older: ChatMessage[], current: ChatMessage[]) => {
  const seen = new Set(current.map((row) => String(row?.messageId || '')).filter(Boolean))
  const dedupedOlder: ChatMessage[] = []
  for (const row of older) {
    const id = String(row?.messageId || '').trim()
    if (id && seen.has(id)) continue
    if (id) seen.add(id)
    dedupedOlder.push(row)
  }
  return [...dedupedOlder, ...current]
}

const mergeMessages = (existing: ChatMessage[], incoming: ChatMessage[]) => {
  const byId = new Map<string, ChatMessage>()
  const withoutIds: ChatMessage[] = []
  const upsert = (row: ChatMessage) => {
    const id = String(row?.messageId || '').trim()
    if (!id) {
      withoutIds.push(row)
      return
    }
    const prev = byId.get(id)
    byId.set(id, prev ? { ...prev, ...row } : row)
  }

  existing.forEach(upsert)
  incoming.forEach(upsert)

  const all = [...byId.values(), ...withoutIds]
  all.sort((a, b) => {
    const aMs = Number.isFinite(Date.parse(String(a?.sentAt || ''))) ? Date.parse(String(a?.sentAt || '')) : 0
    const bMs = Number.isFinite(Date.parse(String(b?.sentAt || ''))) ? Date.parse(String(b?.sentAt || '')) : 0
    return aMs - bMs
  })
  return all
}

const uploadToS3 = async (
  presign: { url: string; fields: Record<string, string> },
  file: File,
) => {
  const form = new FormData()
  Object.entries(presign.fields || {}).forEach(([k, v]) => form.append(k, v))
  form.append('file', file)
  const res = await fetch(presign.url, { method: 'POST', body: form })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text || 'Attachment upload failed')
  }
}

export default function ChatDock({ enabled = true }: ChatDockProps) {
  const loc = useLocation()
  const [profession, setProfession] = React.useState<ProviderProfession>(() => resolveProfession(loc.search))
  const [threads, setThreads] = React.useState<ChatConversation[]>([])
  const [threadsCursor, setThreadsCursor] = React.useState<string | null>(null)
  const [threadsStack, setThreadsStack] = React.useState<ProviderProfession | null>(null)
  const [threadsOpen, setThreadsOpen] = React.useState(false)
  const [loadingMoreThreads, setLoadingMoreThreads] = React.useState(false)
  const [activeConversation, setActiveConversation] = React.useState<ChatConversation | null>(null)
  const [activeConversationStack, setActiveConversationStack] = React.useState<ProviderProfession | null>(null)
  const [minimized, setMinimized] = React.useState(false)
  const [messages, setMessages] = React.useState<ChatMessage[]>([])
  const [messagesCursor, setMessagesCursor] = React.useState<string | null>(null)
  const [draft, setDraft] = React.useState('')
  const [loadingMessages, setLoadingMessages] = React.useState(false)
  const [loadingOlderMessages, setLoadingOlderMessages] = React.useState(false)
  const [sending, setSending] = React.useState(false)
  const [attaching, setAttaching] = React.useState(false)
  const [bookingOpen, setBookingOpen] = React.useState(false)
  const [bookingSlot, setBookingSlot] = React.useState('')
  const [bookingNote, setBookingNote] = React.useState('')
  const [bookingBusy, setBookingBusy] = React.useState(false)
  const [liveUnreadCount, setLiveUnreadCount] = React.useState(0)
  const [error, setError] = React.useState<string | null>(null)

  const fileInputRef = React.useRef<HTMLInputElement | null>(null)
  const bodyRef = React.useRef<HTMLDivElement | null>(null)
  const activeConversationRef = React.useRef<ChatConversation | null>(null)
  const activeConversationStackRef = React.useRef<ProviderProfession | null>(null)
  const minimizedRef = React.useRef(false)
  const professionRef = React.useRef<ProviderProfession>(profession)
  const liveUnreadByConversationRef = React.useRef<Set<string>>(new Set())
  const markReadInFlightRef = React.useRef<Set<string>>(new Set())
  const urlChatHandledRef = React.useRef<{ chatId: string; stack: ProviderProfession } | null>(null)
  const preserveScrollRef = React.useRef(false)
  const preserveHistoryRef = React.useRef(false)
  const threadsStackRef = React.useRef<ProviderProfession | null>(null)
  const loadedOlderThreadsRef = React.useRef(false)
  const urlChatAttemptRef = React.useRef<{ chatId: string; stack: ProviderProfession; at: number } | null>(null)
  const urlChatScanRef = React.useRef<{
    chatId: string
    stack: ProviderProfession
    cursor: string | null
  } | null>(null)

  const unreadTotal = React.useMemo(
    () => threads.reduce((sum, row) => sum + Number(row.unreadCount || 0), 0),
    [threads],
  )

  const flagConversationUnread = React.useCallback((stack: ProviderProfession, conversationId?: string | null) => {
    const id = String(conversationId || '').trim()
    if (!id) return
    const key = chatConversationKey(stack, id)
    if (liveUnreadByConversationRef.current.has(key)) return
    liveUnreadByConversationRef.current.add(key)
    setLiveUnreadCount(liveUnreadByConversationRef.current.size)
  }, [])

  const clearConversationUnread = React.useCallback((stack: ProviderProfession, conversationId?: string | null) => {
    const id = String(conversationId || '').trim()
    if (!id) return
    const key = chatConversationKey(stack, id)
    if (!liveUnreadByConversationRef.current.delete(key)) return
    setLiveUnreadCount(liveUnreadByConversationRef.current.size)
  }, [])

  const refreshThreads = React.useCallback(
    async (stack: ProviderProfession, options: ThreadFetchOptions = {}) => {
      if (!enabled) return null
      const append = Boolean(options.append)
      const preserveExisting = options.preserveExisting !== false
      try {
        const res = await atlas.chatListConversations(stack, {
          limit: options.limit || CHAT_THREADS_PAGE_SIZE,
          cursor: options.cursor || undefined,
        })
        const nextItems = Array.isArray(res?.items) ? res.items : []
        const canAppend = append && threadsStackRef.current === stack
        const canPreserve = !append && preserveExisting && threadsStackRef.current === stack
        if (canAppend) {
          loadedOlderThreadsRef.current = true
        } else if (!canPreserve) {
          loadedOlderThreadsRef.current = false
        }
        setThreads((prev) => {
          const merged = (canAppend || canPreserve)
            ? mergeConversations(prev, nextItems)
            : mergeConversations([], nextItems)
          setActiveConversation((current) => {
            if (!current) return current
            const updated = merged.find((row) => row.conversationId === current.conversationId)
            return updated ? { ...current, ...updated } : current
          })
          return merged
        })
        const fetchedCursor = res?.nextCursor || null
        setThreadsCursor((prev) => {
          if (append) return fetchedCursor
          if (canPreserve) {
            if (loadedOlderThreadsRef.current) {
              return fetchedCursor ? (prev || fetchedCursor) : null
            }
            return fetchedCursor
          }
          return fetchedCursor
        })
        threadsStackRef.current = stack
        setThreadsStack(stack)
        return { items: nextItems, nextCursor: res?.nextCursor || null }
      } catch (err: any) {
        if (!options.quiet) setError(err?.message || 'Failed to load conversations')
        return null
      }
    },
    [enabled],
  )

  const markConversationRead = React.useCallback(
    async (stack: ProviderProfession, conversationId?: string | null) => {
      const id = String(conversationId || '').trim()
      if (!id) return
      const key = chatConversationKey(stack, id)
      if (markReadInFlightRef.current.has(key)) return
      markReadInFlightRef.current.add(key)
      try {
        await atlas.chatMarkRead(stack, id)
        clearConversationUnread(stack, id)
        setActiveConversation((current) => {
          if (!current || current.conversationId !== id) return current
          const currentStack = activeConversationStackRef.current || professionRef.current
          if (currentStack !== stack) return current
          return { ...current, unreadCount: 0 }
        })
        await refreshThreads(stack, { quiet: true, preserveExisting: true })
      } catch {
        // Keep chat UX responsive even if read receipt update fails.
      } finally {
        markReadInFlightRef.current.delete(key)
      }
    },
    [clearConversationUnread, refreshThreads],
  )

  const loadMessages = React.useCallback(
    async (
      stack: ProviderProfession,
      conversation: ChatConversation,
      options: MessageFetchOptions = { markRead: true },
    ) => {
      if (!conversation?.conversationId) return null
      const append = Boolean(options.append)
      if (append) {
        setLoadingOlderMessages(true)
      } else {
        setLoadingMessages(true)
      }
      try {
        const res = await atlas.chatGetMessages(stack, conversation.conversationId, {
          limit: CHAT_MESSAGES_PAGE_SIZE,
          cursor: options.cursor || undefined,
        })
        const nextItems = Array.isArray(res?.items) ? res.items : []
        setMessages((prev) => {
          if (append) return prependMessages(nextItems, prev)
          if (preserveHistoryRef.current) return mergeMessages(prev, nextItems)
          return nextItems
        })
        const fetchedCursor = res?.nextCursor || null
        setMessagesCursor((prev) => {
          if (append) return fetchedCursor
          if (preserveHistoryRef.current) return prev
          return fetchedCursor
        })
        const participantId = String(conversation?.participantId || '').trim()
        const hasUnreadIncoming = !append && Boolean(
          participantId &&
          nextItems.some((row) => row.recipientId === participantId && !row.readAt)
        )
        const shouldMarkRead = !append && (
          options.markRead !== false ||
          (options.autoMarkRead && hasUnreadIncoming)
        )
        if (shouldMarkRead) {
          await markConversationRead(stack, conversation.conversationId)
        }
        return { items: nextItems, nextCursor: res?.nextCursor || null }
      } catch (err: any) {
        setError(err?.message || 'Failed to load chat history')
        return null
      } finally {
        if (append) {
          setLoadingOlderMessages(false)
        } else {
          setLoadingMessages(false)
        }
      }
    },
    [markConversationRead],
  )

  const openConversation = React.useCallback(
    async (stack: ProviderProfession, conversation: ChatConversation, opts: { minimized?: boolean } = {}) => {
      setProfession(stack)
      setActiveConversationStack(stack)
      setActiveConversation(conversation)
      setMinimized(Boolean(opts.minimized))
      setThreadsOpen(false)
      setError(null)
      setBookingOpen(false)
      if (opts.minimized) {
        setMessages([])
        setMessagesCursor(null)
        preserveHistoryRef.current = false
        return
      }
      clearConversationUnread(stack, conversation.conversationId)
      setActiveConversation((current) => {
        if (!current || current.conversationId !== conversation.conversationId) return current
        return { ...current, unreadCount: 0 }
      })
      preserveHistoryRef.current = false
      await loadMessages(stack, conversation, { markRead: true })
    },
    [clearConversationUnread, loadMessages],
  )

  const openFromEvent = React.useCallback(
    async (detail: ChatOpenEventDetail) => {
      if (!enabled) return
      const targetStack = detail.profession && isStackEnabled(detail.profession)
        ? detail.profession
        : resolveProfession(loc.search)

      try {
        setError(null)
        const res = await atlas.chatOpenConversation(targetStack, {
          selectionId: detail.selectionId,
          patientId: detail.patientId,
          specialistId: detail.specialistId,
          peerName: detail.peerName || detail.patientName,
          peerEmail: detail.peerEmail,
          peerPhone: detail.peerPhone,
          patientName: detail.patientName,
        })
        const conversation = res?.conversation
        if (!conversation?.conversationId) throw new Error('Unable to open conversation')

        await openConversation(targetStack, conversation, { minimized: detail.minimized ?? false })
        await refreshThreads(targetStack, { quiet: true, preserveExisting: true })
      } catch (err: any) {
        setError(err?.message || 'Unable to open chat')
      }
    },
    [enabled, loc.search, openConversation, refreshThreads],
  )

  const loadConversationById = React.useCallback(
    async (
      stack: ProviderProfession,
      conversationId: string,
      options: { cursor?: string | null } = {},
    ): Promise<ConversationLookupResult> => {
      let merged = threadsStackRef.current === stack
        ? mergeConversations([], threads)
        : []
      let found = merged.find((row) => row.conversationId === conversationId) || null
      let cursor: string | null = options.cursor || null
      let pages = 0
      const startedAt = Date.now()
      const seenCursors = new Set<string>()

      while (
        !found &&
        pages < CHAT_DEEP_LINK_SCAN_PAGE_BUDGET &&
        Date.now() - startedAt < CHAT_DEEP_LINK_SCAN_MS_BUDGET
      ) {
        pages += 1
        if (cursor && seenCursors.has(cursor)) break
        if (cursor) seenCursors.add(cursor)
        const res = await atlas.chatListConversations(stack, {
          limit: CHAT_DEEP_LINK_PAGE_SIZE,
          cursor: cursor || undefined,
        })
        const nextItems = Array.isArray(res?.items) ? res.items : []
        merged = mergeConversations(merged, nextItems)
        found = merged.find((row) => row.conversationId === conversationId) || null
        cursor = res?.nextCursor || null
        if (!cursor) break
      }

      return {
        found,
        merged,
        cursor,
        pages,
        exhausted: !cursor,
      }
    },
    [threads],
  )

  React.useEffect(() => {
    const next = resolveProfession(loc.search)
    setProfession(next)
  }, [loc.search])

  React.useEffect(() => {
    professionRef.current = profession
  }, [profession])

  React.useEffect(() => {
    activeConversationRef.current = activeConversation
  }, [activeConversation])

  React.useEffect(() => {
    activeConversationStackRef.current = activeConversationStack
  }, [activeConversationStack])

  React.useEffect(() => {
    minimizedRef.current = minimized
  }, [minimized])

  React.useEffect(() => {
    const stack = threadsStack || threadsStackRef.current
    if (!stack || !threads.length) return
    const byId = new Map<string, number>()
    for (const row of threads) {
      const id = String(row?.conversationId || '').trim()
      if (!id) continue
      byId.set(id, Number(row?.unreadCount || 0))
    }
    const next = new Set(liveUnreadByConversationRef.current)
    let changed = false
    for (const key of Array.from(next)) {
      if (!key.startsWith(`${stack}:`)) continue
      const id = key.slice(stack.length + 1)
      if (!byId.has(id)) continue
      if ((byId.get(id) || 0) <= 0) {
        next.delete(key)
        changed = true
      }
    }
    for (const [id, unread] of byId) {
      if (unread <= 0) continue
      const key = chatConversationKey(stack, id)
      if (next.has(key)) continue
      next.add(key)
      changed = true
    }
    if (!changed) return
    liveUnreadByConversationRef.current = next
    setLiveUnreadCount(next.size)
  }, [threads, threadsStack])

  React.useEffect(() => {
    if (!enabled) return
    refreshThreads(profession, { quiet: true, preserveExisting: true })
  }, [enabled, profession, refreshThreads])

  React.useEffect(() => {
    if (!enabled) return
    const onOpen = (event: Event) => {
      const custom = event as CustomEvent<ChatOpenEventDetail>
      openFromEvent(custom.detail || {})
    }
    window.addEventListener('chat:open', onOpen as EventListener)
    return () => window.removeEventListener('chat:open', onOpen as EventListener)
  }, [enabled, openFromEvent])

  const handleRealtimeEvent = React.useCallback(
    (incoming: ChatRealtimeEvent, sourceStack: ProviderProfession) => {
      if (!incoming || typeof incoming !== 'object') return
      const eventType = String(incoming.type || '').trim()
      const stackCandidate = String(incoming.stack || sourceStack || '').trim().toLowerCase()
      const eventStack = isStackEnabled(stackCandidate as ProviderProfession)
        ? (stackCandidate as ProviderProfession)
        : sourceStack
      const conversationId = String(
        incoming.conversationId || incoming.message?.conversationId || ''
      ).trim()
      if (!conversationId || !eventStack) return

      if (eventType === 'chat.message.new') {
        const msg = incoming.message && typeof incoming.message === 'object'
          ? incoming.message
          : null
        const ownUnreadRaw = Number(incoming.ownUnreadCount)
        const ownUnread = Number.isFinite(ownUnreadRaw) ? Math.max(0, ownUnreadRaw) : null
        const lastMessageAt = String(msg?.sentAt || incoming.sentAt || new Date().toISOString())
        const currentStack = activeConversationStackRef.current || professionRef.current
        const conversationIsOpen = Boolean(
          activeConversationRef.current &&
          activeConversationRef.current.conversationId === conversationId &&
          currentStack === eventStack &&
          !minimizedRef.current
        )

        if (threadsStackRef.current === eventStack) {
          setThreads((prev) => {
            const existing = prev.find((row) => row.conversationId === conversationId)
            const fallbackUnread = Number(existing?.unreadCount || 0) + (incoming.direction === 'incoming' ? 1 : 0)
            const patched: ChatConversation = {
              ...(existing || {}),
              conversationId,
              selectionId: incoming.selectionId || existing?.selectionId || null,
              lastMessage: messagePreview(msg),
              lastMessageType: msg?.messageType || existing?.lastMessageType || null,
              lastMessageAt,
              unreadCount: ownUnread ?? fallbackUnread,
            }
            return mergeConversations(prev, [patched])
          })
        }

        setActiveConversation((current) => {
          if (!current || current.conversationId !== conversationId) return current
          const activeStack = activeConversationStackRef.current || professionRef.current
          if (activeStack !== eventStack) return current
          const fallbackUnread = Number(current?.unreadCount || 0) + (incoming.direction === 'incoming' ? 1 : 0)
          return {
            ...current,
            selectionId: incoming.selectionId || current.selectionId || null,
            lastMessage: messagePreview(msg),
            lastMessageType: msg?.messageType || current.lastMessageType || null,
            lastMessageAt,
            unreadCount: ownUnread ?? fallbackUnread,
          }
        })

        if (msg && conversationIsOpen) {
          setMessages((prev) => mergeMessages(prev, [msg]))
          if (incoming.direction === 'incoming') {
            void markConversationRead(eventStack, conversationId)
          }
        } else if (incoming.direction === 'incoming') {
          flagConversationUnread(eventStack, conversationId)
        }

        if (threadsStackRef.current === eventStack || professionRef.current === eventStack) {
          void refreshThreads(eventStack, { quiet: true, preserveExisting: true })
        }
        return
      }

      if (eventType === 'chat.message.read') {
        const currentStack = activeConversationStackRef.current || professionRef.current
        if (
          activeConversationRef.current &&
          activeConversationRef.current.conversationId === conversationId &&
          currentStack === eventStack
        ) {
          const readAt = String(incoming.readAt || new Date().toISOString())
          const me = String(activeConversationRef.current.participantId || '').trim()
          if (!me) return
          setMessages((prev) => prev.map((row) => {
            if (row.senderId !== me) return row
            if (row.readAt) return row
            return { ...row, readAt, status: 'READ' }
          }))
        }
      }
    },
    [flagConversationUnread, markConversationRead, refreshThreads],
  )

  React.useEffect(() => {
    if (!enabled || typeof window === 'undefined') return
    let cancelled = false
    const sockets = new Map<ProviderProfession, WebSocket>()
    const reconnectTimers = new Map<ProviderProfession, number>()
    const reconnectAttempts = new Map<ProviderProfession, number>()

    const clearReconnect = (stack: ProviderProfession) => {
      const timer = reconnectTimers.get(stack)
      if (typeof timer === 'number') {
        window.clearTimeout(timer)
        reconnectTimers.delete(stack)
      }
    }

    const scheduleReconnect = (stack: ProviderProfession) => {
      if (cancelled || reconnectTimers.has(stack)) return
      const nextAttempt = Number(reconnectAttempts.get(stack) || 0) + 1
      reconnectAttempts.set(stack, nextAttempt)
      const delay = Math.min(
        CHAT_REALTIME_RECONNECT_BASE_MS * (2 ** Math.max(0, nextAttempt - 1)),
        CHAT_REALTIME_RECONNECT_MAX_MS,
      )
      const timer = window.setTimeout(() => {
        reconnectTimers.delete(stack)
        void connectStack(stack)
      }, delay)
      reconnectTimers.set(stack, timer)
    }

    const closeSocket = (stack: ProviderProfession) => {
      const existing = sockets.get(stack)
      if (!existing) return
      sockets.delete(stack)
      try {
        existing.onopen = null
        existing.onmessage = null
        existing.onerror = null
        existing.onclose = null
        existing.close()
      } catch {
        // ignore
      }
    }

    const connectStack = async (stack: ProviderProfession) => {
      if (cancelled) return
      clearReconnect(stack)
      closeSocket(stack)
      try {
        const [cfg, token] = await Promise.all([
          atlas.chatRealtimeConfig(stack),
          fetchAuthTokenForRealtime(),
        ])
        if (cancelled) return
        const wsUrl = String(cfg?.wsUrl || '').trim()
        if (!wsUrl || !token) {
          scheduleReconnect(stack)
          return
        }
        const sep = wsUrl.includes('?') ? '&' : '?'
        const url = `${wsUrl}${sep}token=${encodeURIComponent(token)}`
        const ws = new WebSocket(url)
        sockets.set(stack, ws)

        ws.onopen = () => {
          reconnectAttempts.set(stack, 0)
        }

        ws.onmessage = (event) => {
          const parseAndHandle = (raw: string) => {
            try {
              const parsed = JSON.parse(raw) as ChatRealtimeEvent
              handleRealtimeEvent(parsed, stack)
            } catch {
              // ignore invalid payload
            }
          }
          if (typeof event.data === 'string') {
            parseAndHandle(event.data)
            return
          }
          if (event.data instanceof Blob) {
            event.data.text().then(parseAndHandle).catch(() => null)
          }
        }

        ws.onerror = () => {
          try {
            ws.close()
          } catch {
            // ignore
          }
        }

        ws.onclose = () => {
          if (sockets.get(stack) === ws) sockets.delete(stack)
          if (!cancelled) scheduleReconnect(stack)
        }
      } catch {
        if (!cancelled) scheduleReconnect(stack)
      }
    }

    for (const stack of enabledStacks) {
      if (!isStackEnabled(stack)) continue
      void connectStack(stack)
    }

    return () => {
      cancelled = true
      reconnectTimers.forEach((timer) => window.clearTimeout(timer))
      reconnectTimers.clear()
      sockets.forEach((_, stack) => closeSocket(stack))
      sockets.clear()
    }
  }, [enabled, handleRealtimeEvent])

  React.useEffect(() => {
    if (!enabled || !activeConversation || minimized) return
    const stack = activeConversationStack || profession
    loadMessages(stack, activeConversation, { markRead: false, autoMarkRead: true })
    const timer = window.setInterval(() => {
      loadMessages(stack, activeConversation, { markRead: false, autoMarkRead: true })
    }, 5000)
    return () => window.clearInterval(timer)
  }, [
    enabled,
    activeConversation?.conversationId,
    activeConversationStack,
    minimized,
    profession,
    loadMessages,
  ])

  React.useEffect(() => {
    if (!enabled) return
    const timer = window.setInterval(() => {
      refreshThreads(profession, { quiet: true, preserveExisting: true })
    }, 12000)
    return () => window.clearInterval(timer)
  }, [enabled, profession, refreshThreads])

  React.useEffect(() => {
    if (!activeConversation || minimized) return
    if (preserveScrollRef.current) {
      preserveScrollRef.current = false
      return
    }
    const node = bodyRef.current
    if (!node) return
    node.scrollTop = node.scrollHeight
  }, [messages, activeConversation, minimized])

  React.useEffect(() => {
    if (!enabled) return
    const chatId = new URLSearchParams(loc.search).get('chat')
    if (!chatId) {
      urlChatHandledRef.current = null
      urlChatAttemptRef.current = null
      urlChatScanRef.current = null
      return
    }
    const stack = resolveProfession(loc.search)
    if (
      urlChatHandledRef.current &&
      urlChatHandledRef.current.chatId === chatId &&
      urlChatHandledRef.current.stack === stack
    ) return
    const lastAttempt = urlChatAttemptRef.current
    if (
      lastAttempt &&
      lastAttempt.chatId === chatId &&
      lastAttempt.stack === stack &&
      Date.now() - lastAttempt.at < CHAT_DEEP_LINK_RETRY_MS
    ) {
      return
    }

    let cancelled = false
    ;(async () => {
      urlChatAttemptRef.current = { chatId, stack, at: Date.now() }
      try {
        const existing = threadsStackRef.current === stack
          ? threads.find((row) => row.conversationId === chatId) || null
          : null
        let found = existing
        if (!found) {
          const priorScan = urlChatScanRef.current
          const resumeCursor = (
            priorScan &&
            priorScan.chatId === chatId &&
            priorScan.stack === stack
          )
            ? priorScan.cursor
            : null

          const scanned = await loadConversationById(stack, chatId, { cursor: resumeCursor })
          if (cancelled) return

          setThreads(scanned.merged)
          setThreadsCursor(scanned.cursor)
          loadedOlderThreadsRef.current = scanned.pages > 1 || Boolean(resumeCursor)
          threadsStackRef.current = stack
          setThreadsStack(stack)

          found = scanned.found
          if (found || scanned.exhausted) {
            urlChatScanRef.current = null
          } else {
            urlChatScanRef.current = { chatId, stack, cursor: scanned.cursor }
          }

          if (!found) {
            if (scanned.exhausted) {
              setError('Conversation not found')
              urlChatHandledRef.current = { chatId, stack }
            } else {
              setError(null)
            }
            return
          }
        }
        await openConversation(stack, found, { minimized: false })
        if (cancelled) return
        setError(null)
        urlChatHandledRef.current = { chatId, stack }
        urlChatAttemptRef.current = null
        urlChatScanRef.current = null
      } catch {
        if (!cancelled) setError('Unable to open chat')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [enabled, loc.search, threads, loadConversationById, openConversation])

  const closeConversation = () => {
    setActiveConversation(null)
    setActiveConversationStack(null)
    setMessages([])
    setMessagesCursor(null)
    preserveHistoryRef.current = false
    setDraft('')
    setMinimized(false)
    setBookingOpen(false)
    setBookingSlot('')
    setBookingNote('')
  }

  const handleLoadMoreThreads = async () => {
    if (!threadsCursor || loadingMoreThreads) return
    const stack = threadsStack || profession
    setLoadingMoreThreads(true)
    try {
      await refreshThreads(stack, {
        cursor: threadsCursor,
        append: true,
        quiet: false,
      })
    } finally {
      setLoadingMoreThreads(false)
    }
  }

  const handleLoadOlderMessages = async () => {
    if (!activeConversation?.conversationId || !messagesCursor || loadingOlderMessages) return
    const stack = activeConversationStack || profession
    const node = bodyRef.current
    const beforeHeight = node?.scrollHeight || 0
    const beforeTop = node?.scrollTop || 0
    preserveHistoryRef.current = true
    preserveScrollRef.current = true
    await loadMessages(stack, activeConversation, {
      markRead: false,
      cursor: messagesCursor,
      append: true,
    })
    window.requestAnimationFrame(() => {
      if (!node) return
      const afterHeight = node.scrollHeight
      node.scrollTop = Math.max(0, afterHeight - beforeHeight + beforeTop)
    })
  }

  const handleSend = async () => {
    if (!activeConversation?.conversationId || sending) return
    const text = draft.trim()
    if (!text) return

    const stack = activeConversationStack || profession
    setSending(true)
    setError(null)
    try {
      const res = await atlas.chatSendMessage(stack, activeConversation.conversationId, {
        text,
        selectionId: activeConversation.selectionId || undefined,
      })
      if (res?.message) {
        setMessages((prev) => mergeMessages(prev, [res.message]))
      }
      setDraft('')
      await refreshThreads(stack, { quiet: true, preserveExisting: true })
    } catch (err: any) {
      setError(err?.message || 'Failed to send message')
    } finally {
      setSending(false)
    }
  }

  const handleAttach = async (files: FileList | null) => {
    if (!activeConversation?.conversationId || !files?.length || attaching) return
    const stack = activeConversationStack || profession
    setAttaching(true)
    setError(null)

    try {
      for (const file of Array.from(files)) {
        const safeHint = file.name ? file.name.replace(/[^a-z0-9]/gi, '') : 'chatfile'
        const contentType = file.type || 'application/octet-stream'
        const presign = await atlas.presignUpload(stack, safeHint, contentType)
        await uploadToS3(presign, file)
        const key = presign?.key || presign?.fields?.key
        if (!key) throw new Error('Attachment key missing')

        const attachment = {
          key,
          name: file.name || null,
          type: file.type || null,
          size: file.size,
          uploadedAt: new Date().toISOString(),
        }

        const sent = await atlas.chatSendMessage(stack, activeConversation.conversationId, {
          attachments: [attachment],
          selectionId: activeConversation.selectionId || undefined,
        })
        if (sent?.message) {
          setMessages((prev) => mergeMessages(prev, [sent.message]))
        }
      }

      await refreshThreads(stack, { quiet: true, preserveExisting: true })
    } catch (err: any) {
      setError(err?.message || 'Failed to send attachment')
    } finally {
      setAttaching(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleAttachmentOpen = async (key: string) => {
    if (!activeConversation?.conversationId || !key) return
    const stack = activeConversationStack || profession
    try {
      const signed = await atlas.chatSignAttachment(stack, activeConversation.conversationId, key)
      if (signed?.url) {
        window.open(signed.url, '_blank', 'noopener,noreferrer')
      }
    } catch (err: any) {
      setError(err?.message || 'Unable to open attachment')
    }
  }

  const handleCreateBooking = async () => {
    if (!activeConversation?.conversationId || bookingBusy) return
    if (activeConversation.participantRole !== 'specialist') return

    const stack = activeConversationStack || profession
    const slotISO = bookingSlot ? new Date(bookingSlot).toISOString() : CONTACT_SLOT
    const linkedSelectionId = String(activeConversation.selectionId || '').trim()
    if (!linkedSelectionId) {
      setError('This conversation is not linked to a request. Set bookings from the request details page.')
      return
    }

    setBookingBusy(true)
    setError(null)

    try {
      const note = bookingNote.trim()
      const res = await atlas.chatSendMessage(stack, activeConversation.conversationId, {
        text: note,
        booking: {
          slotISO,
          status: 'BOOKED',
          selectionId: linkedSelectionId,
        },
        selectionId: linkedSelectionId,
      })
      if (res?.message) {
        setMessages((prev) => mergeMessages(prev, [res.message]))
      }
      setBookingOpen(false)
      setBookingSlot('')
      setBookingNote('')
      await refreshThreads(stack, { quiet: true, preserveExisting: true })
    } catch (err: any) {
      setError(err?.message || 'Failed to set booking from chat')
    } finally {
      setBookingBusy(false)
    }
  }

  if (!enabled) return null
  const badgeCount = Math.max(unreadTotal, liveUnreadCount)
  const unreadThreadStack = threadsStack || profession
  const unreadThreads = threads.filter((thread) => {
    const conversationId = String(thread?.conversationId || '').trim()
    if (!conversationId) return false
    if (Number(thread?.unreadCount || 0) > 0) return true
    return liveUnreadByConversationRef.current.has(chatConversationKey(unreadThreadStack, conversationId))
  })
  const unreadThreadCount = unreadThreads.length

  const renderThreadLauncher = () => {
    if (activeConversation) return null
    if (!threads.length && badgeCount <= 0) return null
    const threadStack = threadsStack || profession

    return (
      <div className="chat-dock-launcher-wrap">
        <button
          type="button"
          className="chat-dock-launcher"
          onClick={() => setThreadsOpen((prev) => !prev)}
        >
          <MessageSquare size={17} />
          Messages
          {badgeCount > 0 && (
            <span className="chat-dock-badge">{badgeCount > 99 ? '99+' : badgeCount}</span>
          )}
        </button>
        {threadsOpen && (
          <div className="chat-dock-thread-list">
            <div className="chat-dock-thread-list-head">Recent conversations</div>
            {unreadThreadCount > 0 && (
              <div className="chat-dock-thread-alert" role="status" aria-live="polite">
                <span className="chat-dock-thread-alert-dot" aria-hidden />
                {unreadThreadCount === 1
                  ? '1 conversation has a new message'
                  : `${unreadThreadCount} conversations have new messages`}
              </div>
            )}
            <div className="chat-dock-thread-list-body">
              {threads.map((thread) => {
                const conversationId = String(thread.conversationId || '').trim()
                const threadKey = conversationId ? chatConversationKey(threadStack, conversationId) : ''
                const unreadCount = Number(thread.unreadCount || 0)
                const isUnread = unreadCount > 0 || Boolean(threadKey && liveUnreadByConversationRef.current.has(threadKey))
                const unreadLabel = unreadCount > 99 ? '99+' : unreadCount > 0 ? String(unreadCount) : 'New'
                return (
                  <button
                    key={thread.conversationId}
                    type="button"
                    className={`chat-dock-thread-row${isUnread ? ' is-unread' : ''}`}
                    onClick={() => openConversation(threadStack, thread, { minimized: false })}
                  >
                    <span className="chat-dock-thread-avatar">{initials(thread.peerName)}</span>
                    <span className="chat-dock-thread-main">
                      <span className="chat-dock-thread-name">{thread.peerName || 'Client'}</span>
                      <span className="chat-dock-thread-last">{thread.lastMessage || 'Open chat'}</span>
                    </span>
                    <span className="chat-dock-thread-meta">
                      {thread.lastMessageAt ? fmtTime(thread.lastMessageAt) : ''}
                      {isUnread && <strong className="chat-dock-thread-unread-pill">{unreadLabel}</strong>}
                    </span>
                  </button>
                )
              })}
              {threadsCursor && (
                <div className="chat-dock-thread-more-wrap">
                  <button
                    type="button"
                    className="chat-dock-thread-more"
                    onClick={handleLoadMoreThreads}
                    disabled={loadingMoreThreads}
                  >
                    {loadingMoreThreads
                      ? <><Loader2 size={14} className="spin" /> Loading…</>
                      : 'Load older conversations'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderMinimizedBanner = () => {
    if (!activeConversation || !minimized) return null
    const stack = activeConversationStack || profession
    const conversationId = String(activeConversation.conversationId || '').trim()
    const key = conversationId ? chatConversationKey(stack, conversationId) : ''
    const hasUnread = (
      (key && liveUnreadByConversationRef.current.has(key)) ||
      Number(activeConversation.unreadCount || 0) > 0
    )

    return (
      <div className="chat-dock-minimized-wrap">
        <button
          type="button"
          className="chat-dock-minimized"
          onClick={() => openConversation(stack, activeConversation, { minimized: false })}
        >
          <span className="chat-dock-avatar">{initials(activeConversation.peerName)}</span>
          {hasUnread && <span className="chat-dock-dot" aria-label="New messages" />}
          <span className="chat-dock-title">{activeConversation.peerName || 'Client'}</span>
        </button>
        <button
          type="button"
          className="chat-dock-close-btn"
          aria-label="Close chat"
          onClick={closeConversation}
        >
          <X size={14} />
        </button>
      </div>
    )
  }

  const renderExpanded = () => {
    if (!activeConversation || minimized) return null
    const activeConversationId = String(activeConversation.conversationId || '').trim()
    const otherUnreadCount = unreadThreads.reduce((sum, thread) => {
      if (String(thread?.conversationId || '').trim() === activeConversationId) return sum
      return sum + 1
    }, 0)

    return (
      <div className="chat-dock-box" role="dialog" aria-label="Conversation">
        <header className="chat-dock-head">
          <div className="chat-dock-head-main">
            <span className="chat-dock-avatar">{initials(activeConversation.peerName)}</span>
            <div>
              <div className="chat-dock-head-name">{activeConversation.peerName || 'Client'}</div>
              <div className="chat-dock-head-sub">
                Conversation
              </div>
            </div>
          </div>
          <div className="chat-dock-head-actions">
            <button
              type="button"
              className="chat-dock-icon-btn instant-tooltip"
              data-tooltip="Minimize"
              aria-label="Minimize"
              onClick={() => setMinimized(true)}
            >
              <Minimize2 size={15} />
            </button>
            <button
              type="button"
              className="chat-dock-icon-btn instant-tooltip"
              data-tooltip="Close"
              aria-label="Close"
              onClick={closeConversation}
            >
              <X size={15} />
            </button>
          </div>
        </header>

        <div className="chat-dock-body" ref={bodyRef}>
          {otherUnreadCount > 0 && (
            <div className="chat-dock-inline-alert" role="status" aria-live="polite">
              <span className="chat-dock-thread-alert-dot" aria-hidden />
              {otherUnreadCount === 1
                ? '1 other conversation has a new message'
                : `${otherUnreadCount} other conversations have new messages`}
            </div>
          )}
          {messagesCursor && !loadingMessages && (
            <button
              type="button"
              className="chat-dock-load-older"
              onClick={handleLoadOlderMessages}
              disabled={loadingOlderMessages}
            >
              {loadingOlderMessages
                ? <><Loader2 size={14} className="spin" /> Loading…</>
                : 'Load older messages'}
            </button>
          )}

          {loadingMessages && (
            <div className="chat-dock-loading">
              <Loader2 size={15} className="spin" /> Loading…
            </div>
          )}

          {!loadingMessages && !messages.length && (
            <div className="chat-dock-empty">No messages yet. Start the conversation.</div>
          )}

          {messages.map((msg) => {
            const mine = msg.senderId && activeConversation.participantId
              ? msg.senderId === activeConversation.participantId
              : false
            return (
              <article
                key={msg.messageId}
                className={`chat-dock-msg ${mine ? 'mine' : 'theirs'}`}
              >
                <div className="chat-dock-msg-bubble">
                  {msg.text ? <p>{msg.text}</p> : null}

                  {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                    <div className="chat-dock-attachments">
                      {msg.attachments.map((att) => (
                        <button
                          type="button"
                          key={`${msg.messageId}:${att.key}`}
                          className="chat-dock-attachment"
                          onClick={() => handleAttachmentOpen(att.key)}
                        >
                          <Paperclip size={13} />
                          {att.name || 'Attachment'}
                        </button>
                      ))}
                    </div>
                  )}

                  {msg.booking && (
                    <div className="chat-dock-booking-pill">
                      <CalendarPlus size={13} />
                      {msg.booking.slotISO && msg.booking.slotISO !== CONTACT_SLOT
                        ? `Booking ${msg.booking.status || 'update'} · ${fmtTime(msg.booking.slotISO)}`
                        : `Booking ${msg.booking.status || 'update'}`}
                    </div>
                  )}
                </div>
                <div className="chat-dock-msg-meta">
                  <span>{fmtTime(msg.sentAt)}</span>
                  {mine ? <span>{msg.status === 'READ' ? 'Read' : 'Sent'}</span> : null}
                </div>
              </article>
            )
          })}
        </div>

        {error && <div className="chat-dock-error">{error}</div>}

        {bookingOpen && activeConversation.participantRole === 'specialist' && (
          <section className="chat-dock-booking-panel">
            <label>
              Booking time
              <input
                type="datetime-local"
                value={bookingSlot}
                onChange={(e) => setBookingSlot(e.target.value)}
              />
            </label>
            <label>
              Note (optional)
              <textarea
                rows={2}
                value={bookingNote}
                onChange={(e) => setBookingNote(e.target.value)}
                placeholder="Add context for the booking"
              />
            </label>
            <div className="chat-dock-booking-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setBookingOpen(false)}
                disabled={bookingBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateBooking}
                disabled={bookingBusy}
              >
                {bookingBusy ? 'Creating…' : 'Create booking'}
              </button>
            </div>
          </section>
        )}

        <footer className="chat-dock-compose">
          <div className="chat-dock-compose-tools">
            <input
              ref={fileInputRef}
              type="file"
              className="chat-dock-file-input"
              onChange={(e) => handleAttach(e.target.files)}
            />
            <button
              type="button"
              className="chat-dock-tool-btn instant-tooltip"
              data-tooltip="Attach file"
              aria-label="Attach file"
              onClick={() => fileInputRef.current?.click()}
              disabled={attaching || sending}
            >
              {attaching ? <Loader2 size={15} className="spin" /> : <Paperclip size={15} />}
            </button>
            {activeConversation.participantRole === 'specialist' && (
              <button
                type="button"
                className="chat-dock-tool-btn instant-tooltip"
                data-tooltip="Set booking"
                aria-label="Set booking"
                onClick={() => setBookingOpen((prev) => !prev)}
                disabled={bookingBusy || sending}
              >
                <CalendarPlus size={15} />
              </button>
            )}
          </div>

          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`Write a message to ${activeConversation.peerName || 'client'}`}
            rows={2}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
          />

          <button
            type="button"
            className="chat-dock-send"
            onClick={handleSend}
            disabled={sending || !draft.trim()}
            aria-label="Send message"
          >
            {sending ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
          </button>
        </footer>
      </div>
    )
  }

  return (
    <>
      {renderThreadLauncher()}
      {renderMinimizedBanner()}
      {renderExpanded()}
    </>
  )
}
